const initialState = {
  // Multiple dashboards state
  dashboardList: [],           // List of all dashboards
  selectedDashboardId: null,   // Currently selected dashboard ID
  dashboardListLoading: false,
  dashboardModalOpen: false,   // Dashboard create/edit modal
  dashboardModalMode: 'create', // 'create' | 'edit'
  editingDashboard: null,      // Dashboard being edited

  // Dashboard templates state
  templates: [],               // List of available templates
  templatesLoading: false,

  // Dashboard configuration (current dashboard)
  dashboardConfig: null,
  widgets: [],

  // Widget data cache
  widgetData: {},

  // Global filters
  globalFilters: {
    dateRange: {
      preset: 'all_time',
      start: null,
      end: null
    },
    agentIds: [],
    sentiment: [],
    disconnectionReason: [],
    callStatus: [],
    callType: []
  },

  // UI state
  isEditMode: false,
  expandedWidget: null,
  activeModal: null, // 'add' | 'edit' | null
  selectedWidget: null,

  // Loading states
  isLoading: false,
  isSaving: false,
  isLoadingWidgetData: false,
  loadingWidgets: {}, // { widgetId: true/false }

  // Error state
  error: null
};

const Analytics = (state = initialState, { type, payload }) => {
  switch (type) {
    /*========== MULTIPLE DASHBOARDS REDUCERS ============= */

    case 'LIST_DASHBOARDS':
      return { ...state, dashboardListLoading: true, error: null };

    case 'LIST_DASHBOARDS_SUCCESS':
      // Auto-select default or first dashboard if none selected
      const defaultDashboard = payload.find(d => d.isDefault) || payload[0];
      return {
        ...state,
        dashboardList: payload,
        dashboardListLoading: false,
        selectedDashboardId: state.selectedDashboardId || defaultDashboard?._id || null
      };

    case 'LIST_DASHBOARDS_FAILURE':
      return { ...state, dashboardListLoading: false, error: payload };

    case 'SELECT_DASHBOARD':
      return {
        ...state,
        selectedDashboardId: payload,
        dashboardConfig: null,
        widgets: [],
        widgetData: {},
        isLoading: true  // Set loading true when switching dashboards
      };

    case 'CREATE_DASHBOARD':
      return { ...state, isSaving: true };

    case 'CREATE_DASHBOARD_SUCCESS':
      return {
        ...state,
        dashboardList: [...state.dashboardList, payload],
        selectedDashboardId: payload._id,
        dashboardConfig: payload,
        widgets: payload.widgets || [],
        isSaving: false,
        dashboardModalOpen: false
      };

    case 'CREATE_DASHBOARD_FAILURE':
      return { ...state, isSaving: false, error: payload };

    case 'UPDATE_DASHBOARD_DETAILS':
      return { ...state, isSaving: true };

    case 'UPDATE_DASHBOARD_DETAILS_SUCCESS':
      return {
        ...state,
        dashboardList: state.dashboardList.map(d =>
          d._id === payload._id ? { ...d, name: payload.name, description: payload.description } : d
        ),
        dashboardConfig: state.dashboardConfig?._id === payload._id
          ? { ...state.dashboardConfig, name: payload.name, description: payload.description }
          : state.dashboardConfig,
        isSaving: false,
        dashboardModalOpen: false,
        editingDashboard: null
      };

    case 'UPDATE_DASHBOARD_DETAILS_FAILURE':
      return { ...state, isSaving: false, error: payload };

    case 'DELETE_DASHBOARD':
      return { ...state, isSaving: true };

    case 'DELETE_DASHBOARD_SUCCESS':
      const remainingDashboards = state.dashboardList.filter(d => d._id !== payload);
      const deletedWasSelected = state.selectedDashboardId === payload;
      const newSelectedId = deletedWasSelected
        ? (remainingDashboards.find(d => d.isDefault)?._id || remainingDashboards[0]?._id || null)
        : state.selectedDashboardId;
      return {
        ...state,
        dashboardList: remainingDashboards,
        selectedDashboardId: newSelectedId,
        // Clear widgets and show loading when switching to a different dashboard after deletion
        ...(deletedWasSelected && {
          dashboardConfig: null,
          widgets: [],
          widgetData: {},
          isLoading: !!newSelectedId
        }),
        isSaving: false
      };

    case 'DELETE_DASHBOARD_FAILURE':
      return { ...state, isSaving: false, error: payload };

    case 'SET_DEFAULT_DASHBOARD':
      return { ...state, isSaving: true };

    case 'SET_DEFAULT_DASHBOARD_SUCCESS':
      return {
        ...state,
        dashboardList: state.dashboardList.map(d => ({
          ...d,
          isDefault: d._id === payload._id
        })),
        isSaving: false
      };

    case 'SET_DEFAULT_DASHBOARD_FAILURE':
      return { ...state, isSaving: false, error: payload };

    case 'SET_DASHBOARD_MODAL':
      return {
        ...state,
        dashboardModalOpen: payload.open,
        dashboardModalMode: payload.mode || 'create',
        editingDashboard: payload.dashboard || null
      };

    /*========== DASHBOARD TEMPLATES REDUCERS ============= */

    case 'GET_TEMPLATES':
      return { ...state, templatesLoading: true };

    case 'GET_TEMPLATES_SUCCESS':
      return { ...state, templates: payload, templatesLoading: false };

    case 'GET_TEMPLATES_FAILURE':
      return { ...state, templatesLoading: false, error: payload };

    case 'CREATE_DASHBOARD_FROM_TEMPLATE':
      return { ...state, isSaving: true };

    case 'CREATE_DASHBOARD_FROM_TEMPLATE_SUCCESS':
      return {
        ...state,
        dashboardList: [...state.dashboardList, payload],
        selectedDashboardId: payload._id,
        dashboardConfig: payload,
        widgets: payload.widgets || [],
        isSaving: false,
        dashboardModalOpen: false
      };

    case 'CREATE_DASHBOARD_FROM_TEMPLATE_FAILURE':
      return { ...state, isSaving: false, error: payload };

    /*========== DASHBOARD CONFIG REDUCERS ============= */

    case 'GET_DASHBOARD_CONFIG':
      return { ...state, isLoading: true, error: null };

    case 'SET_DASHBOARD_CONFIG':
      // Normalize widgets: convert null agentId to "all" for frontend consistency
      const normalizedWidgets = (payload?.widgets || []).map(widget => ({
        ...widget,
        agentId: widget.agentId || 'all'
      }));
      return {
        ...state,
        dashboardConfig: payload,
        widgets: normalizedWidgets,
        globalFilters: payload?.globalFilters || state.globalFilters,
        isLoading: false
      };

    case 'SAVE_DASHBOARD_CONFIG':
      return { ...state, isSaving: true };

    case 'SAVE_DASHBOARD_CONFIG_SUCCESS':
      return {
        ...state,
        dashboardConfig: payload,
        widgets: payload?.widgets || [],
        isSaving: false
      };

    case 'SAVE_DASHBOARD_CONFIG_FAILURE':
      return { ...state, isSaving: false, error: payload };

    /*========== WIDGET REDUCERS ============= */

    case 'ADD_WIDGET':
      return { ...state, isSaving: true };

    case 'ADD_WIDGET_SUCCESS':
      // Normalize widgets: convert null agentId to "all"
      const addedWidgets = (payload?.widgets || [...state.widgets, payload.widget]).map(widget => ({
        ...widget,
        agentId: widget.agentId || 'all'
      }));
      return {
        ...state,
        widgets: addedWidgets,
        isSaving: false,
        activeModal: null
      };

    case 'UPDATE_WIDGET':
      return { ...state, isSaving: true };

    case 'UPDATE_WIDGET_SUCCESS':
      // Normalize widgets: convert null agentId to "all"
      const updatedWidgets = (payload?.widgets || state.widgets.map(w =>
        w.id === payload.widget.id ? payload.widget : w
      )).map(widget => ({
        ...widget,
        agentId: widget.agentId || 'all'
      }));
      return {
        ...state,
        widgets: updatedWidgets,
        isSaving: false,
        activeModal: null,
        selectedWidget: null
      };

    case 'DELETE_WIDGET':
      return { ...state, isSaving: true };

    case 'DELETE_WIDGET_SUCCESS':
      // Normalize widgets: convert null agentId to "all"
      const deletedWidgets = (payload?.widgets || state.widgets.filter(w => w.id !== payload.widgetId)).map(widget => ({
        ...widget,
        agentId: widget.agentId || 'all'
      }));
      return {
        ...state,
        widgets: deletedWidgets,
        widgetData: { ...state.widgetData, [payload.widgetId]: undefined },
        isSaving: false
      };

    case 'REORDER_WIDGETS':
      return { ...state, isSaving: true };

    case 'REORDER_WIDGETS_SUCCESS':
      // Don't update widgets here - we already updated them locally via REORDER_WIDGETS_LOCAL
      // This prevents the snap-back effect when backend responds
      return {
        ...state,
        isSaving: false
      };

    case 'REORDER_WIDGETS_LOCAL':
      // Immediate local update for smooth drag-drop experience
      return {
        ...state,
        widgets: payload
      };

    /*========== WIDGET DATA REDUCERS ============= */

    case 'GET_WIDGET_DATA':
      return {
        ...state,
        loadingWidgets: { ...state.loadingWidgets, [payload.widgetId]: true }
      };

    case 'SET_WIDGET_DATA':
      return {
        ...state,
        widgetData: { ...state.widgetData, [payload.widgetId]: payload.data },
        loadingWidgets: { ...state.loadingWidgets, [payload.widgetId]: false }
      };

    case 'GET_BATCH_WIDGET_DATA':
      return { ...state, isLoadingWidgetData: true };

    case 'SET_BATCH_WIDGET_DATA':
      return {
        ...state,
        widgetData: { ...state.widgetData, ...payload },
        isLoadingWidgetData: false
      };

    /*========== FILTER REDUCERS ============= */

    case 'SET_DATE_RANGE_FILTER':
      return {
        ...state,
        globalFilters: {
          ...state.globalFilters,
          dateRange: payload
        }
      };

    case 'SET_GLOBAL_FILTERS':
      return {
        ...state,
        globalFilters: {
          ...state.globalFilters,
          ...payload
        }
      };

    case 'RESET_FILTERS':
      return {
        ...state,
        globalFilters: initialState.globalFilters
      };

    /*========== UI STATE REDUCERS ============= */

    case 'SET_EDIT_MODE':
      return { ...state, isEditMode: payload };

    case 'SET_EXPANDED_WIDGET':
      return { ...state, expandedWidget: payload };

    case 'SET_ACTIVE_MODAL':
      return { ...state, activeModal: payload };

    case 'SET_SELECTED_WIDGET':
      return { ...state, selectedWidget: payload };

    /*========== ERROR HANDLING ============= */

    case 'ANALYTICS_ERROR':
      return { ...state, error: payload, isLoading: false, isSaving: false };

    case 'CLEAR_ANALYTICS_ERROR':
      return { ...state, error: null };

    default:
      return state;
  }
};

export default Analytics;
